import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge, StatCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlusIcon, MagnifyingGlassIcon, PackageIcon, WarningIcon, PlusCircleIcon, MinusCircleIcon, TruckIcon, ReceiptIcon } from "@phosphor-icons/react";
import PartsPurchasedPanel from "@/components/PartsPurchasedPanel";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AiPartsReview from "@/components/AiPartsReview";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const CATS = ["general", "filter", "tire", "brake", "engine", "electrical", "fluid", "other"];
const empty = {
  name: "",
  part_number: "",
  category: "general",
  quantity_in_stock: 0,
  unit_cost: 0,
  supplier: "",
  location: "",
  low_stock_threshold: 5,
  notes: "",
};

export default function Inventory() {
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [purchasesOpen, setPurchasesOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ delta: 1, reason: "" });
  const [aiParsed, setAiParsed] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [spend, setSpend] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [allocUnitFilter, setAllocUnitFilter] = useState("all");
  const [allocShowConsumed, setAllocShowConsumed] = useState(false);

  const load = async () => {
    const [p, s, a] = await Promise.all([
      api.get("/parts"),
      api.get("/parts/spend-monthly?months=6"),
      api.get("/parts/allocations"),
    ]);
    setParts(p.data);
    setSpend(s.data);
    setAllocations(a.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        quantity_in_stock: Number(form.quantity_in_stock) || 0,
        unit_cost: Number(form.unit_cost) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
      };
      if (editing) await api.put(`/parts/${editing.id}`, body);
      else await api.post("/parts", body);
      toast.success(editing ? "Part updated" : "Part added");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete part "${p.name}"?`)) return;
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const adjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/parts/${adjustFor.id}/adjust`, {
        delta: Number(adjustForm.delta),
        reason: adjustForm.reason,
      });
      toast.success("Stock adjusted");
      setAdjustFor(null);
      setAdjustForm({ delta: 1, reason: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const filtered = parts.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      (p.part_number || "").toLowerCase().includes(q) ||
      (p.supplier || "").toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    );
  });

  const low_stock = parts.filter((p) => p.quantity_in_stock <= p.low_stock_threshold);
  const total_value = parts.reduce((sum, p) => sum + p.quantity_in_stock * p.unit_cost, 0);

  return (
    <div data-testid="inventory-page">
      <PageHeader eyebrow="Parts inventory" title="Inventory" subtitle="Stockroom. Parts auto-deduct when used in repairs.">
        <Button onClick={() => setPurchasesOpen(true)} variant="outline" className="rounded-sm" data-testid="parts-purchased-button">
          <ReceiptIcon size={16} weight="bold" className="mr-2" /> Parts Purchased
        </Button>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-part-button">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Add Part
        </Button>
      </PageHeader>

      <PartsPurchasedPanel open={purchasesOpen} onClose={() => setPurchasesOpen(false)} />

      <div className="p-8 space-y-6">
        {/* Monthly parts-spend chart */}
        <div className="bg-white border border-slate-300 rounded-sm p-4" data-testid="parts-spend-chart">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">/// Spend</div>
              <div className="font-display text-lg font-black tracking-tight text-slate-900">Parts spend by month</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Last 6 months</div>
              <div className="font-display text-lg font-black tabular-nums text-blue-700">${Math.round(spend.reduce((a, b) => a + Number(b.total || 0), 0)).toLocaleString()}</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={spend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#475569", fontSize: 11, fontFamily: "monospace" }} axisLine={{ stroke: "#cbd5e1" }} />
                <YAxis tick={{ fill: "#475569", fontSize: 11, fontFamily: "monospace" }} axisLine={{ stroke: "#cbd5e1" }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                <Tooltip
                  formatter={(v) => [`$${Number(v).toLocaleString()}`, "Spent"]}
                  contentStyle={{ borderRadius: 2, border: "1px solid #cbd5e1", fontFamily: "monospace", fontSize: 12 }}
                />
                <Bar dataKey="total" fill="#2563eb" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">Combines parts-invoice uploads + parts bought on repair logs.</div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="SKUs" value={parts.length} icon={PackageIcon} testid="stat-skus" />
          <StatCard label="Total Units" value={parts.reduce((s, p) => s + p.quantity_in_stock, 0).toLocaleString()} icon={PackageIcon} testid="stat-units" />
          <StatCard label="Inventory Value" value={`$${total_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} testid="stat-value" />
          <StatCard label="Low Stock" value={low_stock.length} tone={low_stock.length > 0 ? "warning" : "default"} icon={WarningIcon} testid="stat-low-stock" />
        </div>

        <Tabs defaultValue="parts" className="w-full">
          <TabsList className="rounded-sm bg-slate-100 border border-slate-300">
            <TabsTrigger value="parts" className="rounded-sm" data-testid="tab-parts">
              <PackageIcon size={14} weight="bold" className="mr-1.5" /> Parts inventory
            </TabsTrigger>
            <TabsTrigger value="reserved" className="rounded-sm" data-testid="tab-reserved">
              <TruckIcon size={14} weight="bold" className="mr-1.5" /> Reserved for units
              {allocations.filter((a) => a.qty_remaining > 0).length > 0 && (
                <span className="ml-2 bg-emerald-100 text-emerald-800 font-mono text-[10px] px-1.5 py-0.5 rounded-sm">
                  {allocations.filter((a) => a.qty_remaining > 0).length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="parts" className="mt-4">
        <div className="bg-white border border-slate-300 rounded-sm">
          <div className="p-3 border-b border-slate-200 flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search name, part #, supplier, category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="parts-search"
                className="pl-9 rounded-sm border-slate-300"
              />
            </div>
            <span className="font-mono text-xs text-slate-500">{filtered.length} of {parts.length}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <PackageIcon size={40} weight="thin" className="mx-auto text-slate-400 mb-2" />
              <div className="font-mono text-xs text-slate-400 mb-2">/// EMPTY STOCKROOM</div>
              <div className="text-sm text-slate-600">{parts.length === 0 ? "Add your first part to get started." : "No parts match your search."}</div>
            </div>
          ) : (
            <table className="fleet-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Part #</th>
                  <th>Category</th>
                  <th>Stock</th>
                  <th>Unit Cost</th>
                  <th>Value</th>
                  <th>Supplier</th>
                  <th>Location</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const low = p.quantity_in_stock <= p.low_stock_threshold;
                  const out = p.quantity_in_stock === 0;
                  return (
                    <tr key={p.id} data-testid={`part-row-${p.id}`}>
                      <td className="font-semibold">{p.name}</td>
                      <td className="font-mono text-xs text-slate-600">{p.part_number || "—"}</td>
                      <td><Badge tone="neutral">{p.category}</Badge></td>
                      <td>
                        <span className={`font-mono tabular-nums font-semibold ${out ? "text-red-700" : low ? "text-amber-700" : "text-slate-900"}`}>
                          {p.quantity_in_stock}
                        </span>
                        {out ? <Badge tone="danger" testid={`out-${p.id}`}>OUT</Badge> : low ? <Badge tone="warning" testid={`low-${p.id}`}>LOW</Badge> : null}
                      </td>
                      <td className="font-mono tabular-nums">${Number(p.unit_cost).toLocaleString()}</td>
                      <td className="font-mono tabular-nums">${(p.quantity_in_stock * p.unit_cost).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td>{p.supplier || "—"}</td>
                      <td className="font-mono text-xs">{p.location || "—"}</td>
                      <td className="space-x-2 whitespace-nowrap">
                        <button onClick={() => { setAdjustFor(p); setAdjustForm({ delta: 1, reason: "" }); }} className="text-blue-600 hover:underline text-xs font-semibold" data-testid={`adjust-part-${p.id}`}>Adjust</button>
                        <button onClick={() => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); }} className="text-slate-600 hover:underline text-xs font-semibold" data-testid={`edit-part-${p.id}`}>Edit</button>
                        {user?.role === "admin" && (
                          <button onClick={() => del(p)} className="text-red-600 hover:underline text-xs font-semibold" data-testid={`del-part-${p.id}`}>Delete</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
          </TabsContent>

          <TabsContent value="reserved" className="mt-4">
            <ReservedForUnitsTable
              allocations={allocations}
              allocUnitFilter={allocUnitFilter}
              setAllocUnitFilter={setAllocUnitFilter}
              allocShowConsumed={allocShowConsumed}
              setAllocShowConsumed={setAllocShowConsumed}
              onReloadAllocations={load}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="part-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit part" : "Add new part"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="part-name" placeholder="e.g. Engine Oil 15W-40" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Part Number</Label>
              <Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} className="mt-1.5 rounded-sm font-mono" data-testid="part-number" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="part-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Quantity in stock</Label>
              <Input type="number" value={form.quantity_in_stock} onChange={(e) => setForm({ ...form, quantity_in_stock: e.target.value })} className="mt-1.5 rounded-sm" data-testid="part-qty" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Unit cost ($)</Label>
              <Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="mt-1.5 rounded-sm" data-testid="part-cost" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Low-stock threshold</Label>
              <Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="mt-1.5 rounded-sm" data-testid="part-threshold" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Supplier</Label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="mt-1.5 rounded-sm" data-testid="part-supplier" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Location / Bin</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 rounded-sm font-mono" data-testid="part-location" placeholder="e.g. A-12-3" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" data-testid="part-notes" />
            </div>
            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="part-save">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust stock dialog */}
      <Dialog open={!!adjustFor} onOpenChange={(o) => !o && setAdjustFor(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Adjust stock — {adjustFor?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={adjust} className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm">
              Current stock: <span className="font-mono font-bold">{adjustFor?.quantity_in_stock}</span>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Adjustment (use negative to remove)</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setAdjustForm({ ...adjustForm, delta: Number(adjustForm.delta) - 1 })}>
                  <MinusCircleIcon size={16} />
                </Button>
                <Input type="number" value={adjustForm.delta} onChange={(e) => setAdjustForm({ ...adjustForm, delta: e.target.value })} className="text-center rounded-sm font-mono" data-testid="adjust-delta" />
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setAdjustForm({ ...adjustForm, delta: Number(adjustForm.delta) + 1 })}>
                  <PlusCircleIcon size={16} />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Reason</Label>
              <Input value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} placeholder="e.g. Received PO #1234" className="mt-1.5 rounded-sm" data-testid="adjust-reason" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdjustFor(null)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="adjust-save">Apply</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AiPartsReview
        open={aiOpen}
        onOpenChange={setAiOpen}
        parsed={aiParsed}
        onSaved={() => { setAiParsed(null); load(); }}
      />
    </div>
  );
}

// Reserved-for-units table — groups all part allocations by unit for admin visibility.
function ReservedForUnitsTable({ allocations, allocUnitFilter, setAllocUnitFilter, allocShowConsumed, setAllocShowConsumed, onReloadAllocations }) {
  const [threshold, setThreshold] = useState(2000);
  const [catThresholds, setCatThresholds] = useState({});
  const [busyRelease, setBusyRelease] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    api.get("/company/preferences").then(({ data }) => {
      if (data?.reserved_alert_threshold != null) setThreshold(Number(data.reserved_alert_threshold));
      if (data?.category_thresholds) setCatThresholds(data.category_thresholds || {});
    }).catch(() => {});
  }, []);

  const releaseOne = async (a) => {
    if (!window.confirm(`Release ${a.qty_remaining} × "${a.part_name}" from Unit ${a.unit_number} back to general stock?`)) return;
    setBusyRelease(true);
    try {
      const { data } = await api.post(`/parts/allocations/${a.id}/release`);
      toast.success(data.message || "Released");
      onReloadAllocations?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Release failed");
    } finally { setBusyRelease(false); }
  };
  const releaseAllForUnit = async (u) => {
    if (!window.confirm(`Release ALL ${u.count_open} open reservation(s) for Unit ${u.unit_number} (${u.unit_type === "truck" ? "Truck" : "Trailer"}) back to general stock?`)) return;
    setBusyRelease(true);
    try {
      const { data } = await api.post(`/parts/allocations/release-unit`, { unit_id: u.unit_id, unit_type: u.unit_type });
      toast.success(`Released ${data.released_count} reservation(s) — ${data.released_qty} unit(s) returned to stock`);
      onReloadAllocations?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Release failed");
    } finally { setBusyRelease(false); }
  };
  const filtered = useMemo(() => {
    let list = allocations || [];
    if (!allocShowConsumed) list = list.filter((a) => Number(a.qty_remaining) > 0);
    if (allocUnitFilter !== "all") {
      const [ut, uid] = allocUnitFilter.split(":");
      list = list.filter((a) => a.unit_type === ut && a.unit_id === uid);
    }
    return list;
  }, [allocations, allocShowConsumed, allocUnitFilter]);

  // Group by unit for the summary and dropdown
  const byUnit = useMemo(() => {
    const m = {};
    (allocations || []).forEach((a) => {
      const key = `${a.unit_type}:${a.unit_id}`;
      if (!m[key]) m[key] = { unit_type: a.unit_type, unit_id: a.unit_id, unit_number: a.unit_number, count_open: 0, count_all: 0, value_open: 0, by_category: {} };
      m[key].count_all += 1;
      if (Number(a.qty_remaining) > 0) {
        m[key].count_open += 1;
        const rem = Number(a.qty_remaining) * Number(a.unit_cost);
        m[key].value_open += rem;
        const cat = (a.part_category || "other").toLowerCase();
        m[key].by_category[cat] = (m[key].by_category[cat] || 0) + rem;
      }
    });
    return Object.values(m).sort((a, b) => (a.unit_number || "").localeCompare(b.unit_number || ""));
  }, [allocations]);

  const totalReservedValue = byUnit.reduce((s, u) => s + u.value_open, 0);
  const totalReservedRows = byUnit.reduce((s, u) => s + u.count_open, 0);
  // Compute flags — per-category override wins; otherwise fall back to default.
  const flaggedUnits = byUnit.map((u) => {
    const catBreaches = Object.entries(u.by_category)
      .filter(([cat, val]) => {
        const thr = catThresholds[cat];
        // If a category override exists, only that override applies to that category (no double-counting against default).
        if (thr != null) return val >= thr && thr > 0;
        return false;
      })
      .map(([cat, val]) => ({ category: cat, value: val, threshold: catThresholds[cat] }));
    // Default threshold applies to categories WITHOUT an override, in aggregate.
    const uncoveredValue = Object.entries(u.by_category)
      .filter(([cat]) => catThresholds[cat] == null)
      .reduce((s, [, v]) => s + v, 0);
    const defaultBreach = threshold > 0 && uncoveredValue >= threshold;
    return { ...u, catBreaches, uncoveredValue, defaultBreach, flagged: catBreaches.length > 0 || defaultBreach };
  }).filter((u) => u.flagged);

  return (
    <div className="space-y-4" data-testid="reserved-for-units-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Units with reserved parts" value={byUnit.filter((u) => u.count_open > 0).length} icon={TruckIcon} testid="stat-units-reserved" />
        <StatCard label="Open reservations" value={totalReservedRows} icon={PackageIcon} testid="stat-open-reservations" />
        <StatCard label="Reserved value" value={`$${Math.round(totalReservedValue).toLocaleString()}`} testid="stat-reserved-value" />
        <StatCard label="Total allocations logged" value={allocations.length} testid="stat-total-allocations" />
      </div>

      {/* Threshold alert banner */}
      {flaggedUnits.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-sm p-3" data-testid="reserved-threshold-alert">
          <div className="flex items-start gap-3 mb-2">
            <WarningIcon size={18} weight="fill" className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-amber-900 text-sm">
                {flaggedUnits.length} unit{flaggedUnits.length > 1 ? "s have" : " has"} stale reservations above threshold
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Consider using these parts on scheduled work or releasing them back to general stock.
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {flaggedUnits.map((u) => {
              const catText = u.catBreaches.map((c) => `${c.category} $${Math.round(c.value).toLocaleString()} > $${Math.round(c.threshold).toLocaleString()}`).join(" · ");
              const defText = u.defaultBreach ? `default $${Math.round(u.uncoveredValue).toLocaleString()} > $${Math.round(threshold).toLocaleString()}` : "";
              const reason = [catText, defText].filter(Boolean).join(" · ");
              return (
                <div key={`${u.unit_type}:${u.unit_id}`} className="flex items-center justify-between bg-white/60 border border-amber-300 rounded-sm px-2.5 py-1.5" data-testid={`flagged-unit-${u.unit_type}-${u.unit_id}`}>
                  <div className="text-xs">
                    <span className="font-bold text-amber-900">
                      {u.unit_type === "truck" ? "🚚 Unit" : "🚛 Trailer"} {u.unit_number}
                    </span>
                    <span className="text-amber-800 ml-2 font-mono text-[11px]">{reason}</span>
                    <span className="ml-3 text-amber-700 text-[11px]">{u.count_open} open · ${Math.round(u.value_open).toLocaleString()}</span>
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      onClick={() => releaseAllForUnit(u)}
                      disabled={busyRelease}
                      className="h-7 rounded-sm bg-amber-700 hover:bg-amber-800 text-white text-xs font-bold px-2"
                      data-testid={`release-unit-${u.unit_type}-${u.unit_id}`}
                    >
                      Move all to stock
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-300 rounded-sm">
        <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <span className="section-eyebrow text-slate-500">/// Filter</span>
          <select
            value={allocUnitFilter}
            onChange={(e) => setAllocUnitFilter(e.target.value)}
            className="h-8 border border-slate-300 rounded-sm text-xs px-2 bg-white"
            data-testid="alloc-unit-filter"
          >
            <option value="all">All units ({byUnit.length})</option>
            {byUnit.map((u) => (
              <option key={`${u.unit_type}:${u.unit_id}`} value={`${u.unit_type}:${u.unit_id}`}>
                {u.unit_type === "truck" ? "🚚" : "🚛"} {u.unit_type === "truck" ? "Unit" : "Trailer"} {u.unit_number} · {u.count_open} open
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={allocShowConsumed} onChange={(e) => setAllocShowConsumed(e.target.checked)} data-testid="alloc-show-consumed" />
            Show fully-consumed
          </label>
          <span className="ml-auto font-mono text-xs text-slate-500">{filtered.length} row{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <TruckIcon size={40} weight="thin" className="mx-auto text-slate-400 mb-2" />
            <div className="font-mono text-xs text-slate-400 mb-2">/// NO RESERVATIONS</div>
            <div className="text-sm text-slate-600">
              Parts get reserved for a unit when you assign them during a parts-invoice upload.
            </div>
          </div>
        ) : (
          <table className="fleet-table">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Part</th>
                <th>Part #</th>
                <th>Category</th>
                <th className="text-right">Purchased</th>
                <th className="text-right">Remaining</th>
                <th className="text-right">In stock</th>
                <th className="text-right">Unit $</th>
                <th className="text-right">Reserved value</th>
                <th>Invoice</th>
                <th>Purchased</th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const remaining = Number(a.qty_remaining) || 0;
                const purchased = Number(a.qty_purchased) || 0;
                const used = purchased - remaining;
                return (
                  <tr key={a.id} data-testid={`alloc-row-${a.id}`}>
                    <td className="whitespace-nowrap">
                      <div className="font-semibold text-slate-900">
                        {a.unit_type === "truck" ? "🚚" : "🚛"} {a.unit_type === "truck" ? "Unit" : "Trailer"} {a.unit_number}
                      </div>
                    </td>
                    <td className="font-semibold">{a.part_name}</td>
                    <td className="font-mono text-xs text-slate-600">{a.part_number || "—"}</td>
                    <td>{a.part_category ? <Badge tone="neutral">{a.part_category}</Badge> : "—"}</td>
                    <td className="font-mono tabular-nums text-right">{purchased}</td>
                    <td className="font-mono tabular-nums text-right">
                      <span className={remaining === 0 ? "text-slate-400" : "font-bold text-emerald-700"}>{remaining}</span>
                      {used > 0 && remaining > 0 && <span className="font-mono text-[10px] text-amber-600 ml-1">({used} used)</span>}
                      {remaining === 0 && <Badge tone="neutral">DONE</Badge>}
                    </td>
                    <td className="font-mono tabular-nums text-right text-slate-600">{a.current_stock ?? "—"}</td>
                    <td className="font-mono tabular-nums text-right">${Number(a.unit_cost).toFixed(2)}</td>
                    <td className="font-mono tabular-nums text-right font-bold">${(remaining * Number(a.unit_cost)).toFixed(2)}</td>
                    <td className="font-mono text-xs text-slate-600">{a.invoice_number || "—"}</td>
                    <td className="font-mono text-xs text-slate-500">{a.purchased_at || "—"}</td>
                    {isAdmin && (
                      <td className="text-right">
                        {remaining > 0 ? (
                          <button
                            type="button"
                            onClick={() => releaseOne(a)}
                            disabled={busyRelease}
                            className="text-blue-700 hover:text-blue-900 hover:underline text-xs font-semibold disabled:opacity-50"
                            data-testid={`release-alloc-${a.id}`}
                            title="Return these parts to general stock (they stay in inventory, just un-earmark them)"
                          >
                            Move to stock
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs font-mono">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

